/**
 * HOME — WHAT IS WAITING TODAY (item 1).
 *
 * CTS 1 opens with a greeting, a row of tiles and "Needs attention today". So
 * does this, and for the same reason: the first screen of the morning should
 * say what needs a person, not what the database contains.
 *
 * Two things this screen does that CTS 1 does not. Every group says whether
 * the user may SEE it (`shown`) — a queue drawn empty because you lack the
 * function is a lie, so it is not drawn at all. And the two subjects that are
 * not modelled yet (testing warnings, production and stock) say so in words
 * rather than showing a reassuring empty list (D231).
 */
import * as React from 'react';
import { api } from '../api.js';
import { useAsk, go } from '../hooks.js';
import { Banner, Card, Count, Kpi, Loading, Money, Pill } from '../ui.js';
import { Page } from '../shell.js';
import { count, date, days, greeting, longDate, money, qty, when, words } from '../format.js';
// the same words the full findings queue uses, so Home and Operations agree
import { findingWords } from '../standing.js';

import { screenById, primaryRole } from '../../../src/screens/contracts.js';
import { CeoHome, SalesHome, AccountsHome, OperatorHome, HealthBoard } from './dashboards.js';
import { type Session } from '../api.js';

/** 10 Oct (QA ISSUE-005): a finding's entity is a table name; the front page says what it is, not where it lives. */
function entityWords(table: string): string {
  const KNOWN: Record<string, string> = {
    'platform.message_kinds': 'the document outbox', 'platform.messages': 'the document outbox',
    'billing.customer_balances': 'a customer balance', 'billing.charges': 'a charge', 'billing.bills': 'a bill',
    'custody.challans': 'a challan', 'custody.lendings': 'a trip', 'registry.cylinders': 'a cylinder',
  };
  return KNOWN[table] ?? words(table.split('.').pop() ?? table);
}

const MESSAGES = screenById('home')!.messages;

/** 0173 — one front page per job. The branch manager, and anybody without a business role, keeps the KPI dashboard below. */
export function Home({ session, canDispatch }: { session: Session; canDispatch: boolean }) {
  switch (primaryRole(session.roles ?? [])) {
    case 'CEO': return <CeoHome session={session} />;
    case 'SALES_MANAGER': return <SalesHome session={session} />;
    case 'ACCOUNTANT': return <AccountsHome session={session} />;
    case 'OPERATOR': return <OperatorHome session={session} />;
    default: return <BranchHome canDispatch={canDispatch} />;
  }
}

function BranchHome({ canDispatch }: { canDispatch: boolean }) {
  const asked = useAsk(() => api.view('home.worklist', {}), [], MESSAGES);

  if (asked.loading) return <Page title="Home"><Loading what="today's work" /></Page>;
  if (asked.error) return <Page title="Home"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Page>;
  const w = asked.data!;

  const actions = canDispatch ? (
    <>
      <button className="btn primary" onClick={() => go('#/move?dir=out')}>Dispatch</button>
      <button className="btn good" onClick={() => go('#/move?dir=in')}>Receive back</button>
    </>
  ) : null;

  return (
    <Page title={`${greeting()}`} sub={longDate()} actions={actions}>
      {/* 12 Oct — WHAT THIS COMPANY STILL NEEDS. The owner opened seven empty
        * screens and said the same of each: confusing and incomplete. Each was
        * right and none of them said whether empty was good news or a job not
        * done. This says which, once, and goes away when there is nothing left. */}
      <SetupSteps />
      {/* 14 Oct — CTS1's health board, the same one every role sees, cut to the three
        * groups a branch manager can actually act on. */}
      <HealthBoard groups={['Tracking', 'Filling', 'Compliance']} />
      <div className="kpis">
        {w.ordersToApprove.shown ? <Kpi label="Orders to approve" value={count(w.ordersToApprove.total)} tone="primary" note="waiting for approval" href="#/approvals" /> : null}
        {w.pendingApprovals.shown ? <Kpi label="Loads to check" value={count(w.pendingApprovals.total)} tone="empty" note="prepared, not yet approved" href="#/approvals" /> : null}
        {w.loadsToDepart.shown ? <Kpi label="Ready to leave" value={count(w.loadsToDepart.total)} tone="good" note="approved, still at the plant" href="#/departure" /> : null}
        {w.deliveriesAwaitingConfirmation.shown ? <Kpi label="Out for delivery" value={count(w.deliveriesAwaitingConfirmation.total)} tone="primary" note="on the road, no proof yet" href="#/delivery" /> : null}
        {/* FIRST, AND IN RED, because it is the only figure on this page that is a safety
            matter: D267 says an overdue vessel must not be filled. It is shown only when
            there is one — a zero in red every morning teaches people to ignore red. */}
        {w.testingDue.shown && (w.testingDue.overdue + w.testingDue.failed) > 0 ? (
          <Kpi label="Test overdue" value={count(w.testingDue.overdue + w.testingDue.failed)} tone="danger"
               note="must not be filled" href="#/cylinders?test=overdue" />
        ) : null}
        {w.damageToSettle.shown && w.damageToSettle.total > 0 ? (
          <Kpi label="Damage to settle" value={count(w.damageToSettle.total)} tone="money"
               note={w.damageToSettle.disputed > 0 ? `${w.damageToSettle.disputed} disputed` : 'awaiting a decision'}
               href="#/casework?queue=damage" />
        ) : null}
        {w.quarantined.shown && w.quarantined.total > 0 ? (
          <Kpi label="Held out of service" value={count(w.quarantined.total)} tone="danger"
               note="quarantined" href="#/casework?queue=quarantine" />
        ) : null}
        {w.cylindersOverdue.shown ? <Kpi label="Overdue returns" value={count(w.cylindersOverdue.total)} tone="danger" note={`held past ${w.cylindersOverdue.thresholdDays} days`} href="#/billing" /> : null}
        {w.accountsOwing.shown ? <Kpi label="Accounts owing" value={count(w.accountsOwing.total)} tone="money" note="customers with money outstanding" href="#/billing" /> : null}
      </div>

      <h2 style={{ fontSize: 16, margin: '4px 0 12px' }}>Needs attention today</h2>
      <div className="grid three">
        {w.ordersToApprove.shown ? (
          <Card title="Orders to approve" sub={`${count(w.ordersToApprove.total)} waiting`}
                actions={<a href="#/approvals" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go('#/approvals'); }}>all of them →</a>}>
            {w.ordersToApprove.rows.length === 0 ? <p className="muted">Nothing waiting.</p> : w.ordersToApprove.rows.slice(0, 4).map((r) => (
              <a className="row-link" key={r.orderId} href={`#/orders/${r.orderId}`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/orders/${r.orderId}`); }}>
                <span>
                  <strong>{r.customerName}</strong>
                  <span>{r.siteName} · {qty(r.qty, r.unit)} · wanted {date(r.requestedDate)}</span>
                </span>
                <span className="chev" aria-hidden="true">&rsaquo;</span>
              </a>
            ))}
          </Card>
        ) : null}

        {w.pendingApprovals.shown ? (
          <Card title="Loads to check" sub={`${count(w.pendingApprovals.total)} waiting`}
                actions={<a href="#/approvals" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go('#/approvals'); }}>the worklist →</a>}>
            {w.pendingApprovals.rows.length === 0 ? <p className="muted">Nothing waiting.</p> : w.pendingApprovals.rows.slice(0, 4).map((r) => (
              <a className="row-link" key={r.challanId} href={`#/dispatch/${r.challanId}`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/dispatch/${r.challanId}`); }}>
                <span>
                  <strong>{r.customerName}</strong>
                  <span>{r.type === 'return' ? 'Return' : 'Dispatch'} · {count(r.lineCount)} cyl · {date(r.eventDate)}</span>
                </span>
                <span className="chev" aria-hidden="true">&rsaquo;</span>
              </a>
            ))}
          </Card>
        ) : null}

        {w.loadsToDepart.shown ? (
          <Card title="Ready to leave" sub={`${count(w.loadsToDepart.total)} approved`}
                actions={<a href="#/departure" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go('#/departure'); }}>all of them →</a>}>
            {w.loadsToDepart.rows.length === 0 ? <p className="muted">Nothing at the gate.</p> : w.loadsToDepart.rows.slice(0, 4).map((r) => (
              <a className="row-link" key={r.challanId} href={`#/departure/${r.challanId}`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/departure/${r.challanId}`); }}>
                <span>
                  <strong>{r.customerName}</strong>
                  <span>{r.siteName} · {count(r.lineCount)} cyl{r.vehicle ? ` · ${r.vehicle}` : ''}</span>
                </span>
                <span className="chev" aria-hidden="true">&rsaquo;</span>
              </a>
            ))}
          </Card>
        ) : null}

        {w.deliveriesAwaitingConfirmation.shown ? (
          <Card title="Out for delivery" sub={`${count(w.deliveriesAwaitingConfirmation.total)} without proof`}
                actions={<a href="#/delivery" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go('#/delivery'); }}>all of them →</a>}>
            {w.deliveriesAwaitingConfirmation.rows.length === 0 ? <p className="muted">Every load is accounted for.</p> : w.deliveriesAwaitingConfirmation.rows.slice(0, 4).map((r) => (
              <a className="row-link" key={r.challanId} href={`#/delivery/${r.challanId}`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/delivery/${r.challanId}`); }}>
                <span>
                  <strong>{r.customerName}</strong>
                  <span>{r.number ?? 'no number'} · left {date(r.departedOn)} · {days(r.daysOut)} out</span>
                </span>
                <span className="chev" aria-hidden="true">&rsaquo;</span>
              </a>
            ))}
          </Card>
        ) : null}

        {w.cylindersUnaccounted.shown && w.cylindersUnaccounted.total > 0 ? (
          <Card title="Cylinders unaccounted" sub={`${count(w.cylindersUnaccounted.total)} to settle`}>
            {w.cylindersUnaccounted.rows.slice(0, 4).map((r) => (
              <a className="row-link" key={r.cylinderId} href={`#/cylinders/${r.cylinderId}`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/cylinders/${r.cylinderId}`); }}>
                <span><strong className="serial">{r.internalId}</strong><span>{r.serialNo} · {days(r.daysUnaccounted)} unaccounted</span></span>
                <span className="chev" aria-hidden="true">&rsaquo;</span>
              </a>
            ))}
          </Card>
        ) : null}

        {w.accountsOwing.shown ? (
          <Card title="Accounts owing" sub={`${count(w.accountsOwing.total)} customers`}
                actions={<a href="#/billing" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go('#/billing'); }}>all of them →</a>}>
            {w.accountsOwing.rows.length === 0 ? <p className="muted">Nothing outstanding.</p> : w.accountsOwing.rows.slice(0, 4).map((r) => (
              <a className="row-link" key={r.customerId} href={`#/customers/${r.customerId}?tab=bills`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/customers/${r.customerId}?tab=bills`); }}>
                <span>
                  <strong>{r.customerName}</strong>
                  <span className="money">{money(r.payablePaise)}</span>
                  {r.overdue
                    ? <span> · <Pill tone="danger">{`${r.daysOverdue} ${r.daysOverdue === 1 ? 'day' : 'days'} overdue`}</Pill></span>
                    : r.owesWithinTerms && r.oldestDueOn !== null
                      ? <span> · <Pill tone="muted">{`due ${r.oldestDueOn}`}</Pill></span>
                      : null}
                </span>
                <span className="chev" aria-hidden="true">&rsaquo;</span>
              </a>
            ))}
          </Card>
        ) : null}

        {/* ---------------------------------------------------------------- 4 October 2026
            FOUR SLICES' WORK THAT NEVER REACHED THIS PAGE.
            Damage, quarantine, yards and labels were each built with their own screen and
            their own permissions, and none of them appeared on the one page an operator
            opens first. A branch manager with a case awaiting assessment had to already
            know to go and look, which makes "action before info" true of every screen and
            false of the product: an action nobody can see is not an action.
            Each card shows only when there is something in it, so a quiet day is a short
            page rather than a wall of zeroes. */}
        {w.testingDue.shown && (w.testingDue.overdue + w.testingDue.failed + w.testingDue.dueSoon) > 0 ? (
          <Card title="Test overdue or due soon"
                sub={[w.testingDue.overdue ? `${w.testingDue.overdue} overdue` : '',
                      w.testingDue.failed ? `${w.testingDue.failed} failed` : '',
                      w.testingDue.dueSoon ? `${w.testingDue.dueSoon} due within ${w.testingDue.thresholdDays} days` : '']
                      .filter(Boolean).join(' · ')}>
            {w.testingDue.rows.slice(0, 4).map((r) => (
              <a className="row-link" key={r.cylinderId} href={`#/cylinders/${r.cylinderId}`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/cylinders/${r.cylinderId}`); }}>
                <span>
                  <strong className="serial">{r.internalId}</strong>
                  <span>{r.serialNo}
                    {' · '}
                    {/* The WORDS, not the status token: "overdue" and "failed" mean different
                        things to a filler and neither should arrive as a database value. */}
                    {r.testStatus === 'overdue' ? <Pill tone="danger">{r.nextTestDue ? `test was due ${r.nextTestDue}` : 'test overdue'}</Pill>
                      : r.testStatus === 'failed' ? <Pill tone="danger">failed its last test</Pill>
                      : <Pill tone="empty">{r.daysUntilDue === null ? 'due soon' : `due in ${r.daysUntilDue} ${r.daysUntilDue === 1 ? 'day' : 'days'}`}</Pill>}
                  </span>
                </span>
                <span className="chev" aria-hidden="true">&rsaquo;</span>
              </a>
            ))}
          </Card>
        ) : null}

        {w.damageToSettle.shown && w.damageToSettle.total > 0 ? (
          <Card title="Damage to settle" sub={`${count(w.damageToSettle.total)} open`}
                actions={<a href="#/casework?queue=damage" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go('#/casework?queue=damage'); }}>all of them &rarr;</a>}>
            {w.damageToSettle.rows.slice(0, 4).map((r) => (
              <a className="row-link" key={r.damageId} href={`#/casework?queue=damage&case=${r.damageId}`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/casework?queue=damage&case=${r.damageId}`); }}>
                <span>
                  <strong className="serial">{r.internalId}</strong>
                  <span>{r.customerName ?? 'no customer on the return'}
                    {' · '}
                    {r.status === 'disputed' ? <Pill tone="danger">the customer disputes it</Pill>
                      : r.status === 'awaiting_assessment' ? <Pill tone="money">needs a figure</Pill>
                      : <Pill tone="empty">reported</Pill>}
                  </span>
                </span>
                <span className="chev" aria-hidden="true">&rsaquo;</span>
              </a>
            ))}
          </Card>
        ) : null}

        {w.quarantined.shown && w.quarantined.total > 0 ? (
          <Card title="Held out of service" sub={`${count(w.quarantined.total)} quarantined`}
                actions={<a href="#/casework?queue=quarantine" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go('#/casework?queue=quarantine'); }}>all of them &rarr;</a>}>
            {w.quarantined.rows.slice(0, 4).map((r) => (
              <a className="row-link" key={r.cylinderId} href={`#/cylinders/${r.cylinderId}`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/cylinders/${r.cylinderId}`); }}>
                <span>
                  <strong className="serial">{r.internalId}</strong>
                  <span>{r.reason} · {days(r.days)}</span>
                </span>
                <span className="chev" aria-hidden="true">&rsaquo;</span>
              </a>
            ))}
            {w.quarantined.forDispute > 0 ? (
              <p className="muted">{w.quarantined.forDispute} of these are held for an ownership dispute. Lifting a quarantine does not settle one — deciding whose cylinder it is does.</p>
            ) : null}
          </Card>
        ) : null}

        {w.yardsToSettle.shown && w.yardsToSettle.total > 0 ? (
          <Card title="Standing somewhere unrecorded" sub={`${count(w.yardsToSettle.total)} to settle`}
                actions={<a href="#/casework?queue=yards" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go('#/casework?queue=yards'); }}>all of them &rarr;</a>}>
            {w.yardsToSettle.rows.slice(0, 4).map((r) => (
              <a className="row-link" key={r.cylinderId} href={`#/cylinders/${r.cylinderId}`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/cylinders/${r.cylinderId}`); }}>
                <span>
                  <strong className="serial">{r.internalId}</strong>
                  <span>{r.serialNo} · <Pill tone="empty">yard not recorded</Pill>
                    {r.homeBranchCode ? ` · owned by ${r.homeBranchCode}` : ''}</span>
                </span>
                <span className="chev" aria-hidden="true">&rsaquo;</span>
              </a>
            ))}
            <p className="muted">Where a vessel is owned is not where it is standing, so these were deliberately not assumed. Until one is settled in writing, a branch-scoped decision for it is refused.</p>
          </Card>
        ) : null}

        {w.labelsToPrint.shown && w.labelsToPrint.total > 0 ? (
          <Card title="Labels waiting on a person" sub={`${count(w.labelsToPrint.total)} sheet(s)`}
                actions={<a href="#/labels" onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go('#/labels'); }}>the label register &rarr;</a>}>
            {w.labelsToPrint.rows.slice(0, 4).map((r) => (
              <a className="row-link" key={r.batchId} href={`#/labels/${r.batchId}`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/labels/${r.batchId}`); }}>
                <span>
                  <strong>{count(r.labels)} label(s)</strong>
                  <span>{r.stockCode ?? 'no stock named'}
                    {' · '}
                    {/* CTS cannot see a printer and never claims to: `printed` is a PERSON's
                        statement, so the gap between generated and printed is work waiting
                        on one rather than a job the system can finish by itself. */}
                    {r.status === 'prepared' ? <Pill tone="empty">chosen, no sheet yet</Pill>
                      : <Pill tone="primary">printed? say so</Pill>}
                  </span>
                </span>
                <span className="chev" aria-hidden="true">&rsaquo;</span>
              </a>
            ))}
          </Card>
        ) : null}

        {w.findings.shown && w.findings.total > 0 ? (
          <Card title="Something needs a look" sub={`${count(w.findings.critical)} serious`}>
            {w.findings.rows.slice(0, 4).map((r) => {
              const cyl = /^registry\.cylinders:(\d+)$/.exec(r.entity ?? '');
              const inner = <span><strong>{findingWords(r.checkName)}</strong><span>{cyl ? `cylinder ${cyl[1]}` : r.entity ? entityWords(r.entity.split(':')[0] ?? '') : 'the system'} · {r.severity === 'critical' ? 'serious' : r.severity}{r.overdue ? ' · overdue' : ''}</span></span>;
              return cyl
                ? <a className="row-link" key={r.findingId} href={`#/cylinders/${cyl[1]}`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/cylinders/${cyl[1]}`); }}>{inner}<span className="chev" aria-hidden="true">&rsaquo;</span></a>
                : <div className="row-link" key={r.findingId}>{inner}</div>;
            })}
          </Card>
        ) : null}
      </div>

      {/* THIS LINE USED TO SAY "Testing due dates and filling stock are not in CTS yet",
          and half of it stopped being true when Book 3 landed (0108-0111). A stale
          disclaimer on the front page reads as diligence and is the most expensive kind of
          wrong text: it told an operator the system did not know a cylinder's test status
          while the database could answer it, about the one fact here that is a safety
          matter. Filling and tank stock really are a later slice (D259), so that half
          stays — and says which, rather than lumping them together. */}
      <p className="muted" style={{ marginTop: 16 }}>
        Counted at {when(w.asOf)}.
        {w.testingDue.shown && (w.testingDue.overdue + w.testingDue.failed + w.testingDue.dueSoon) === 0
          ? ' No cylinder is overdue for test or due within the month.'
          : ''}
        {' '}Filling and tank stock are not in CTS yet.
      </p>
    </Page>
  );
}

/**
 * THE STEPS A NEW COMPANY STILL HAS TO TAKE, in the order they unblock each
 * other. Shown only while something is outstanding: a company that is set up
 * never sees it, and a company that chooses never to open a second branch is
 * not nagged about things that are merely useful.
 */
function SetupSteps() {
  const asked = useAsk(() => api.view('setup.state', {}), [], MESSAGES);
  const d = asked.data;
  if (!d || d.outstanding === 0) return null;
  const left = d.steps.filter((s) => !s.done);
  return (
    <Card title={d.blocking > 0 ? 'Before this company can trade' : 'Worth finishing'}
          sub={d.blocking > 0
            ? `${count(d.blocking)} thing(s) still stop a cylinder going out — each one is a screen away`
            : 'nothing is blocked; these are the last touches'}>
      <ol className="setup-steps">
        {left.map((s) => (
          <li key={s.key} className={s.blocking ? 'blocking' : undefined}>
            <div className="setup-head">
              <b>{s.title}</b>
              {s.blocking ? <Pill tone="danger">needed first</Pill> : <Pill tone="empty">when you can</Pill>}
              <button type="button" className="btn ghost small" onClick={() => go(s.where)}>{s.action}</button>
            </div>
            <div className="muted">{s.why}</div>
          </li>
        ))}
      </ol>
    </Card>
  );
}
